// frontend/src/components/players/PDFViewer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSecureMedia } from '../../services/mediaService';
import * as pdfjsLib from 'pdfjs-dist';

// تنظیم worker
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

// تنظیمات بهینه برای سرعت بالا
const PDF_JS_OPTIONS = {
  // ✅ فقط صفحات مورد نیاز دانلود بشه
  disableAutoFetch: true,
  // ✅ دانلود تدریجی
  disableStream: false,
  // ✅ کش کردن
  useWorkerFetch: true,
  // ✅ فونت‌های سیستم
  useSystemFonts: true,
  // ✅ CMap برای فارسی
  cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/',
  // ✅ محدود کردن حجم
  maxImageSize: 512 * 1024, // 512KB
  // ✅ غیرفعال کردن XFA برای سرعت
  enableXfa: false,
  // ✅ متوقف نشدن در خطا
  stopAtErrors: false,
  // ✅ کش فونت
  fontExtraProperties: false,
  isEvalSupported: false,
};

const PDFViewer = ({ mediaId, fileUrl, title }) => {
  const { mediaUrl, isLoading: urlLoading, error: urlError, refetch: refetchMediaUrl } =
    useSecureMedia(mediaId);

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [renderProgress, setRenderProgress] = useState(0);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const loadingTaskRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isMounted = useRef(true);

  const directPdf = fileUrl || mediaUrl;
  const proxyUrl = directPdf?.startsWith('https://s3.afra.kubit.cloud/')
    ? `https://farzaneh.app/api/proxy/media/?url=${encodeURIComponent(directPdf)}`
    : directPdf;

  // اندازه‌گیری عرض کانتینر
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth - 40);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // ✅ بارگذاری سریع PDF
  useEffect(() => {
    if (!proxyUrl) return;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setRenderProgress(0);
        
        // لغو بارگذاری قبلی
        if (loadingTaskRef.current) {
          loadingTaskRef.current.destroy();
        }
        if (renderTaskRef.current) {
          await renderTaskRef.current.promise.then(() => {}, () => {});
        }

        // ✅ بارگذاری با اولویت صفحات اول
        const loadingTask = pdfjsLib.getDocument({
          url: proxyUrl,
          ...PDF_JS_OPTIONS,
          withCredentials: false,
          verbosity: 0,
          // ✅ اولویت با صفحات اول
          rangeChunkSize: 64 * 1024, // 64KB
        });

        loadingTaskRef.current = loadingTask;

        // ✅ پیشرفت بارگذاری
        loadingTask.onProgress = (progress) => {
          if (isMounted.current) {
            const percent = Math.round((progress.loaded / progress.total) * 100);
            setRenderProgress(Math.min(percent, 99));
          }
        };

        const pdf = await loadingTask.promise;
        
        if (isMounted.current) {
          setPdfDoc(pdf);
          setNumPages(pdf.numPages);
          setPageNumber(1);
          setRenderProgress(100);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading PDF:', error);
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      isMounted.current = false;
      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy();
      }
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
      if (pdfDoc) {
        pdfDoc.destroy();
      }
    };
  }, [proxyUrl]);

  // ✅ رندر سریع صفحه (با کیفیت مناسب)
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current || !containerWidth) return;

    try {
      // لغو رندر قبلی
      if (renderTaskRef.current) {
        await renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      const page = await pdfDoc.getPage(pageNumber);
      
      // ✅ محاسبه scale بهینه برای سرعت (کیفیت متعادل)
      const viewport = page.getViewport({ scale: 1 });
      const calculatedScale = (containerWidth) / viewport.width;
      const finalScale = Math.min(Math.max(calculatedScale, 0.7), 1.8);
      
      // ✅ کاهش کیفیت برای سرعت بیشتر (1.5x به جای 2x)
      const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
      const renderScale = finalScale * devicePixelRatio;

      const scaledViewport = page.getViewport({ scale: renderScale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d', { 
        alpha: false,
        willReadFrequently: false,
      });

      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      canvas.style.width = `${scaledViewport.width / devicePixelRatio}px`;
      canvas.style.height = `${scaledViewport.height / devicePixelRatio}px`;

      // ✅ تنظیمات رندر با سرعت بالا
      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
        background: '#fff',
        enableWebGL: false,
        renderInteractiveForms: false,
        enableXfa: false,
        intent: 'display',
        annotationMode: 0, // غیرفعال کردن annotation برای سرعت
      };

      const renderTask = page.render(renderContext);
      renderTaskRef.current = renderTask;
      
      await renderTask.promise;
      renderTaskRef.current = null;
      
      setScale(finalScale);
    } catch (error) {
      if (error.name !== 'RenderingCancelledException') {
        console.error('Error rendering page:', error);
      }
    }
  }, [pdfDoc, pageNumber, containerWidth]);

  useEffect(() => {
    if (pdfDoc && containerWidth && !loading) {
      renderPage();
    }
  }, [renderPage, pdfDoc, containerWidth, loading]);

  const changePage = useCallback((offset) => {
    setPageNumber(prev => {
      const newPage = prev + offset;
      return Math.max(1, Math.min(newPage, numPages));
    });
  }, [numPages]);

  const goToPage = (page) => {
    const newPage = Math.max(1, Math.min(page, numPages));
    setPageNumber(newPage);
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  const zoomFit = () => {
    if (containerRef.current && pdfDoc) {
      renderPage();
    }
  };

  // جلوگیری از دانلود
  const handleContextMenu = (e) => { e.preventDefault(); };
  const handleDragStart = (e) => { e.preventDefault(); };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p')) {
      e.preventDefault();
    }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      changePage(1);
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      changePage(-1);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [changePage]);

  // وضعیت خطا
  if (urlError) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <p className="font-bold text-red-400 mb-4">{urlError}</p>
        <button onClick={() => refetchMediaUrl?.()} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
          تلاش مجدد
        </button>
      </div>
    );
  }

  // ✅ نمایش پیشرفت بارگذاری
  if (urlLoading || loading || !proxyUrl) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 mb-2">در حال بارگذاری...</p>
        <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${renderProgress}%` }}
          />
        </div>
        <p className="text-xs text-gray-400 mt-2">{renderProgress}%</p>
      </div>
    );
  }

  return (
    <div 
      className="h-screen flex flex-col bg-gray-100 select-none"
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
    >
      {/* هدر */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 text-white">
        <h2 className="text-base font-semibold truncate max-w-xs">
          {title || 'کتاب'}
        </h2>
        <div className="flex items-center gap-3 text-sm">
          <span>صفحه {pageNumber}</span>
          <span className="opacity-50">از {numPages}</span>
        </div>
      </div>

      {/* نمایش PDF */}
      <div 
        ref={containerRef}
        className="flex-1 flex items-center justify-center bg-gray-100 p-4 overflow-auto"
      >
        <div className="relative shadow-2xl">
          <canvas ref={canvasRef} className="bg-white max-w-full" />
          {/* آبی‌نگار */}
          <div className="absolute inset-0 pointer-events-none opacity-5 flex items-center justify-center">
            <span className="text-4xl font-bold text-gray-500 rotate-[-30deg]">
              FARZANEH
            </span>
          </div>
        </div>
      </div>

      {/* کنترل‌ها */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 text-white">
        <div className="flex items-center gap-1">
          <button onClick={zoomOut} className="p-1.5 rounded hover:bg-gray-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1v-1z" />
            </svg>
          </button>
          <span className="text-xs min-w-[50px] text-center">{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} className="p-1.5 rounded hover:bg-gray-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 10v6m-3-3h6" />
            </svg>
          </button>
          <button onClick={zoomFit} className="px-2 py-1 text-xs rounded hover:bg-gray-700">
            Fit
          </button>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => changePage(-1)}
            disabled={pageNumber <= 1}
            className="p-1.5 rounded hover:bg-gray-700 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <input
            type="number"
            min={1}
            max={numPages}
            value={pageNumber}
            onChange={(e) => {
              const val = parseInt(e.target.value);
              if (val >= 1 && val <= numPages) goToPage(val);
            }}
            className="w-12 px-1 py-1 bg-gray-700 text-white text-center rounded border border-gray-600 focus:outline-none focus:border-blue-500 text-sm"
          />
          <span className="text-xs opacity-50">/ {numPages}</span>
          <button
            onClick={() => changePage(1)}
            disabled={pageNumber >= numPages}
            className="p-1.5 rounded hover:bg-gray-700 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <button
          onClick={() => {
            const elem = document.querySelector('.pdf-viewer-container');
            if (elem?.requestFullscreen) elem.requestFullscreen();
          }}
          className="p-1.5 rounded hover:bg-gray-700"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PDFViewer;